import {
  AutomationStatus,
  MessageStatus,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import styled from '@emotion/styled';
import { differenceWith, isEqual } from 'lodash';

import { ChipFilterWithDropdown } from '../../../../components/ChipFilterWithDropdown';
import { TableSearch } from '../../../../components/Table';
import { useUnsafeTranslation, UnsafeTFunction } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { SearchFilter } from '../../pages/Details/ExecutionReportTab';
import { FilterButton, SearchInput } from '../Builders/newux/kit';
import { getFilterButtonState } from '../Builders/newux/kit/filterButtonState';
import { StatusChip } from '../StatusChip';

type StatusStats = {
  status: string;
  count: number;
};

const SubTitle = styled('div')((p) => ({
  color: p.theme.custom.colors.gray.w500,
  fontSize: p.theme.custom.fontSize.m,
  fontWeight: p.theme.typography.fontWeightMedium
}));

const ChipWrapper = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  cursor: 'pointer'
}));

const StatusSelector = ({
  selectedValue,
  onChange,
  stats,
  experienceTemplateId,
  initialSearchFilter,
  elementId,
  type,
  title
}: {
  selectedValue: string[];
  onChange: (value: string[]) => void;
  stats: StatusStats[];
  experienceTemplateId: string;
  initialSearchFilter: Array<string> | undefined;
  elementId: string;
  type: 'AUTOMATION' | 'TASK' | 'MESSAGE';
  title: string;
}) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  return (
    <FlexContainer>
      <SubTitle>{t('Filter')}</SubTitle>
      {stats.map((stat: StatusStats) => {
        const selected = selectedValue.includes(stat.status);
        const count = stat.count;
        if (count <= 0) {
          return null;
        }
        return (
          <ChipWrapper
            key={stat.status}
            onClick={() => {
              const nextSelectedValue = selected
                ? selectedValue.filter((x) => x !== stat.status)
                : [...selectedValue, stat.status];

              onChange(nextSelectedValue);

              trackEvent({
                eventType: PAEventType.EXPERIENCE_TPL,
                name: 'Timeline Item Modal People Tab Filters Applied',
                'Experience Template ID': experienceTemplateId,
                Explanation:
                  'Admin user filters which statuses appear in the people tab for a timeline item',
                'Timeline Item ID': elementId,
                'Timeline Item Type': type,
                'Timeline Item Title': title,
                'Filter Changed': stat.status,
                'Selected or Deselected': selected ? 'Selected' : 'Deselected',
                'Selected Filters': nextSelectedValue,
                'Deselected Filters': differenceWith(
                  initialSearchFilter,
                  nextSelectedValue,
                  isEqual
                )
              });
            }}
          >
            <StatusChip
              status={stat.status}
              selected={selected}
              count={count}
            />
          </ChipWrapper>
        );
      })}
    </FlexContainer>
  );
};

/** Plain status label (no count) for the NEW_UX status dropdown filter. */
const statusFilterLabel = (status: string, t: UnsafeTFunction): string => {
  switch (status) {
    case MessageStatus.UPCOMING:
    case TaskStatus.NOT_STARTED:
    case AutomationStatus.UPCOMING:
      return t('Upcoming');
    case TaskStatus.RUNNING:
      return t('Todo');
    case TaskStatus.LATE:
      return t('Overdue');
    case MessageStatus.ERROR:
    case AutomationStatus.ERROR:
      return t('Error');
    case AutomationStatus.DONE:
    case TaskStatus.COMPLETE:
      return t('Done');
    case 'OK':
      return t('Sent');
    case TaskStatus.SKIPPED:
    case AutomationStatus.SKIPPED:
    case MessageStatus.SKIPPED:
      return t('Skipped');
    case 'PAST_DEADLINE':
      return t('Past deadline');
    default:
      return status;
  }
};

const FilterBar = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: p.theme.spacing()
}));

/** NEW_UX status filter: a "Status" dropdown (ChipFilterWithDropdown) instead
 * of the count chips, plus the kit search box — matches the task-viewer design. */
const NewUxExecutionReportHeader = ({
  filter,
  onStatusChange,
  onNameChange,
  stats
}: {
  filter: SearchFilter;
  stats: StatusStats[];
  onNameChange: (value: string) => any;
  onStatusChange: (value: string[]) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const options = stats
    .filter((s) => s.count > 0)
    .map((s) => ({ key: s.status, label: statusFilterLabel(s.status, t) }));
  const optionKeys = options.map((o) => o.key);
  const selectedOptions = options.filter((o) => filter.status.includes(o.key));
  // Statuses in the filter that have no visible option (e.g. PAST_DEADLINE with
  // no rows) are preserved on change so behavior doesn't shift.
  const hiddenSelected = filter.status.filter((s) => !optionKeys.includes(s));

  const { active, label } = getFilterButtonState(
    options,
    selectedOptions.map((o) => o.key),
    t('Status'),
    (count) => t('{{ count }} statuses', { count })
  );

  return (
    <FilterBar>
      <ChipFilterWithDropdown
        label={t('Status')}
        options={options}
        selectedOptions={selectedOptions}
        onChange={(xs) =>
          onStatusChange([...xs.map((x) => x.key), ...hiddenSelected])
        }
        renderTrigger={({ onClick }) => (
          <FilterButton label={label} active={active} onClick={onClick} />
        )}
        exclusiveAll
      />
      <SearchInput
        value={filter.name || ''}
        onChange={onNameChange}
        placeholder={t('Search by name')}
      />
    </FilterBar>
  );
};

export const ExecutionReportHeader: React.FC<{
  filter: SearchFilter;
  stats: StatusStats[];
  onNameChange: (value: string) => any;
  onStatusChange: (value: string[]) => void;
  experienceTemplateId: string;
  initialSearchFilter: Array<string> | undefined;
  elementId: string;
  type: 'AUTOMATION' | 'TASK' | 'MESSAGE';
  title: string;
  /** NEW_UX: render the Status dropdown + kit search instead of the count chips. */
  newUx?: boolean;
}> = ({
  filter,
  onStatusChange,
  onNameChange,
  stats,
  experienceTemplateId,
  initialSearchFilter,
  elementId,
  type,
  title,
  newUx
}) => {
  const { t } = useUnsafeTranslation();

  if (newUx) {
    return (
      <NewUxExecutionReportHeader
        filter={filter}
        stats={stats}
        onNameChange={onNameChange}
        onStatusChange={onStatusChange}
      />
    );
  }

  return (
    <FlexContainer justifyContent="space-between" fullWidth>
      <StatusSelector
        stats={stats}
        selectedValue={filter.status}
        onChange={onStatusChange}
        type={type}
        title={title}
        elementId={elementId}
        initialSearchFilter={initialSearchFilter}
        experienceTemplateId={experienceTemplateId}
      />

      <TableSearch
        placeholder={t('Search by name')}
        term={filter.name || ''}
        onChange={onNameChange}
      />

      {/* Add column selector component here */}
    </FlexContainer>
  );
};
